const FAVORITES_PREFIX = 'teks-time-report-favorites';
const USAGE_PREFIX = 'teks-time-report-usage';

export interface ReportUsageEntry {
  path: string;
  count: number;
  lastVisitedAt: string;
}

function favoritesKey(userId?: string): string {
  return userId ? `${FAVORITES_PREFIX}-${userId}` : FAVORITES_PREFIX;
}

function usageKey(userId?: string): string {
  return userId ? `${USAGE_PREFIX}-${userId}` : USAGE_PREFIX;
}

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore storage errors
  }
}

export function getReportFavorites(userId?: string): string[] {
  return readJson<string[]>(favoritesKey(userId), []);
}

export function isReportFavorite(userId: string | undefined, path: string): boolean {
  return getReportFavorites(userId).includes(path);
}

export function toggleReportFavorite(userId: string | undefined, path: string): string[] {
  const current = getReportFavorites(userId);
  const next = current.includes(path)
    ? current.filter((item) => item !== path)
    : [...current, path];
  writeJson(favoritesKey(userId), next);
  return next;
}

export function getReportUsage(userId?: string): ReportUsageEntry[] {
  return readJson<ReportUsageEntry[]>(usageKey(userId), []);
}

export function recordReportVisit(userId: string | undefined, path: string): void {
  if (!path || path === '/reports') return;
  const usage = getReportUsage(userId);
  const existing = usage.find((entry) => entry.path === path);
  const nextEntry: ReportUsageEntry = existing
    ? {
      path,
      count: existing.count + 1,
      lastVisitedAt: new Date().toISOString(),
    }
    : {
      path,
      count: 1,
      lastVisitedAt: new Date().toISOString(),
    };
  const next = [nextEntry, ...usage.filter((entry) => entry.path !== path)].slice(0, 20);
  writeJson(usageKey(userId), next);
}

export function getRecentReportPaths(userId: string | undefined, limit = 8): string[] {
  return getReportUsage(userId)
    .sort((a, b) => new Date(b.lastVisitedAt).getTime() - new Date(a.lastVisitedAt).getTime())
    .slice(0, limit)
    .map((entry) => entry.path);
}

export function formatReportLastVisited(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function getReportUsageEntry(userId: string | undefined, path: string): ReportUsageEntry | undefined {
  return getReportUsage(userId).find((entry) => entry.path === path);
}
